import React, { FC, forwardRef, useState } from 'react';
import { Theme, useMediaQuery, useTheme } from '@mui/material';
import { FooterAccordion, FooterAccordionProps } from './FooterAccordion';

export interface FooterMenuProps extends Omit<FooterAccordionProps, 'variant' | 'children'> {
  /**
   * Items in the menu
   */
  children?: React.ReactNode;
  /**
   * Variant of the menu
   * - auto: renders on large resolution as list and on mobile as accordion
   * - list: renders as list with title
   * - accordion: renders as collapsible accordion
   * @default auto
   */
  variant?: 'auto' | 'list' | 'accordion';
}

/**
 * Section for the secondary links, copyright and optional button
 */
export const FooterMenu: FC<FooterMenuProps> = forwardRef(
  ({ sx, children, label, accordionSummaryProps, contentProps, variant = 'auto', ...rest }, ref) => {
    const { sx: contentSx, ...restContent } = contentProps ?? {};
    const { sx: accSummarySx, ...restAccSummary } = accordionSummaryProps ?? {};
    const theme = useTheme();
    const downLg = useMediaQuery<Theme>(theme.breakpoints.down('lg'));
    const isAccordion = variant === 'auto' ? downLg : variant === 'accordion';
    const [expanded, setExpanded] = useState(false);

    return (
      <FooterAccordion
        key={`${isAccordion}`}
        ref={ref}
        data-testid="FooterMenu"
        expanded={isAccordion ? expanded : true}
        onChange={(_, v) => setExpanded(v)}
        variant="standard"
        sx={{
          // spacing in the FooterSectionMenu, take max 1/5 of space
          flexBasis: { lg: '20%' },
          borderColor: 'onSurface.os700',
          ...(!isAccordion && {
            border: 'none',
          }),
          ...sx,
        }}
        accordionSummaryProps={{
          ['data-testid']: 'FooterMenu.label',
          sx: {
            // adjust proportions to be closer to menu
            padding: '18px 0 8px',
            // manipulates style of links inside
            '& .MuiLink-root': {
              ...theme.typography.subheadline,
              p: 0,
              '&, &:hover': {
                textDecoration: 'none',
                color: 'common.white',
              },
            },
            ...(!isAccordion && {
              // allow overflow of focus ring
              overflow: 'visible',
              // no longer clickable
              '&&:not(.Mui-disabled)': {
                cursor: 'unset',
              },
              // hide icon
              '& .MuiAccordionSummary-expandIconWrapper': {
                display: 'none',
              },
            }),
            ...accSummarySx,
          },
          ...restAccSummary,
        }}
        label={label}
        contentProps={{
          component: 'div',
          ['data-testid']: 'FooterMenu.content',
          sx: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            gap: 1.5,
            // manipulates style of links inside
            '& .MuiLink-root': {
              ...theme.typography.caption2,
              p: 0,
              '&, &:hover': {
                textDecoration: 'none',
                color: 'onSurface.os200',
              },
            },
            ...contentSx,
          },
          ...restContent,
        }}
        {...rest}
      >
        {children}
      </FooterAccordion>
    );
  },
);
